import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { alpha, Box, type SxProps, type Theme, useTheme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { ALPHA, DISABLED_CONTROL, FONT_SIZE, ICON_SIZE, INK, MOTION, NEUTRAL, SHADOW, SHEET_ROW, SURFACE } from "@/theme/tokens";
import { JumpingDots } from "../Animations/JumpingDots";
import { AccentIconTile } from "../molecules/AccentIconTile";

export type SheetActionRowTone = "default" | "danger";

/**
 * What the row IS to assistive tech. `action` is a plain button; `switch` is an on/off setting the row flips on
 * tap (and draws its own indicator); `radio` is one choice in a group the caller wraps in `role="radiogroup"`.
 */
export type SheetActionRowKind = "action" | "switch" | "radio";

type LeadingProps =
	/** A glyph, drawn in the row's own tile — tinted by the row's state, so danger and on read without extra props. */
	| { icon?: ReactNode; leading?: never }
	/** Anything else in the leading column (a role badge, an avatar). Replaces the tile. */
	| { leading?: ReactNode; icon?: never };

type KindProps =
	/** A plain button — the kind a row is when none is given. It has no on/off state. */
	| { kind?: "action"; checked?: never }
	/**
	 * An on/off setting (`switch`) or one choice in a group (`radio`). Its state is REQUIRED: a missing one would announce
	 * `aria-checked="false"` and draw the row off, which reads as a real answer.
	 */
	| { kind: Exclude<SheetActionRowKind, "action">; checked: boolean };

type Props = LeadingProps &
	KindProps & {
		/** Already translated. Also the row's accessible name, together with `description`. */
		label: string;
		/** A second, quieter line under the label — a consequence, a current value, the note being edited. */
		description?: ReactNode;
		/** Right-aligned value (the current unit, the hours, a `filled/required`). Tabular, muted by default. */
		trailing?: ReactNode;
		/** A chevron after the trailing value: this row leads somewhere deeper rather than acting in place. */
		showChevron?: boolean;
		tone?: SheetActionRowTone;
		onClick: () => void;
		disabled?: boolean;
		/** The row's write is in flight: dots replace the trailing value and further taps are ignored. */
		loading?: boolean;
		dataTestId?: string;
		sx?: SxProps<Theme>;
	};

/**
 * One full-width row of a bottom sheet or touch menu: a leading tile, a label with an optional line under it, a
 * trailing value, and optionally a chevron — the "what can I do here" list every phone action sheet is made of.
 *
 * Built because the alternative was each sheet hand-rolling `ButtonBase` rows with their own paddings and glyph
 * sizes, which is how two sheets opened from the same screen end up looking like two apps. The row owns the
 * geometry (`SHEET_ROW`), the inset hairline, the press/hover feedback and the three semantics a sheet row can
 * have (`kind`); the caller owns the words and what a tap does.
 *
 * `switch` rows draw a small presentational track rather than embedding a real switch: the row is the control, so a
 * nested input would be interactive content inside interactive content. `danger` tints the tile and label with the
 * error palette; a checked row tints its tile with the brand, so the on-state is visible in the leading column too.
 */
export const SheetActionRow: FC<Props> = ({
	label,
	description,
	trailing,
	// DEFAULT: each optional flag's absence is the plain row — no chevron, the default tone, a plain action, enabled,
	// idle. None of them stands in for missing data; `checked`, which would, is required for the kinds that have it.
	showChevron = false,
	tone = "default",
	kind = "action",
	checked,
	onClick,
	disabled = false,
	loading = false,
	icon,
	leading,
	dataTestId,
	sx,
}) => {
	const theme = useTheme();
	const isDanger = tone === "danger";
	const isOn = kind !== "action" && checked === true;
	// A chosen option says so across the whole row — a brand wash and a trailing check — not only in its tile: a row that
	// leads with a badge instead of a tile (a person's role) would otherwise show its choice as a bolder label alone.
	const isChosen = kind === "radio" && checked === true;
	const tileColor = isDanger ? theme.palette.error.main : isOn ? theme.palette.primary.dark : INK.SECONDARY;
	// On reads one step stronger than a resting brand tile: beside neutral tiles, the resting brand wash was too close
	// to grey to register as a state.
	const tileTint = isDanger
		? alpha(theme.palette.error.main, ALPHA.BRAND_TINT)
		: isOn
			? alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER)
			: NEUTRAL[150];

	return (
		<Box
			component="button"
			type="button"
			role={kind === "action" ? undefined : kind}
			aria-checked={kind === "action" ? undefined : checked}
			aria-busy={loading || undefined}
			disabled={disabled}
			data-testid={dataTestId}
			// A tap while the write is in flight is swallowed rather than disabling the row: disabling would drop focus
			// and grey the row out mid-save, which reads as "that failed".
			onClick={loading ? undefined : onClick}
			sx={[
				{
					position: "relative",
					display: "flex",
					alignItems: "center",
					gap: `${SHEET_ROW.LEADING_GAP}px`,
					width: "100%",
					minHeight: SHEET_ROW.MIN_HEIGHT,
					boxSizing: "border-box",
					px: `${SHEET_ROW.PADDING_X}px`,
					py: 1,
					border: 0,
					bgcolor: isChosen ? alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) : SURFACE,
					fontFamily: "inherit",
					textAlign: "start",
					color: INK.PRIMARY,
					cursor: "pointer",
					WebkitTapHighlightColor: "transparent",
					transition: `background-color ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
					// Inset hairline: it starts past the leading column so the tiles read as one column.
					"&::after": {
						content: '""',
						position: "absolute",
						left: SHEET_ROW.DIVIDER_INSET,
						right: 0,
						bottom: 0,
						height: "1px",
						bgcolor: NEUTRAL[150],
					},
					"@media (hover: hover) and (pointer: fine)": {
						"&:hover:not(:disabled)": { bgcolor: isChosen ? alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER) : NEUTRAL[50] },
					},
					"&:active:not(:disabled)": { bgcolor: isChosen ? alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER) : NEUTRAL[150] },
					"&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: "-2px" },
					"&:disabled": { cursor: "default", opacity: DISABLED_CONTROL.DIMMED_OPACITY },
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{leading ??
				(icon !== undefined && (
					<AccentIconTile size="xs" component="span" color={tileColor} tint={tileTint} sx={{ "& > svg": { fontSize: ICON_SIZE.MENU_ROW } }}>
						{icon}
					</AccentIconTile>
				))}
			<Box component="span" sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
				<Box
					component="span"
					sx={{
						fontSize: FONT_SIZE.ROW_LABEL,
						fontWeight: isDanger || isOn ? 600 : 500,
						lineHeight: 1.3,
						color: isDanger ? "error.main" : INK.PRIMARY,
						overflowWrap: "anywhere",
					}}
				>
					{label}
				</Box>
				{description !== undefined && (
					<Box component="span" sx={{ mt: "2px", fontSize: FONT_SIZE.CAPTION, lineHeight: 1.4, color: INK.MUTED, overflowWrap: "anywhere" }}>
						{description}
					</Box>
				)}
			</Box>
			{loading ? (
				<JumpingDots />
			) : (
				trailing !== undefined && (
					<Box
						component="span"
						sx={{ flex: "none", maxWidth: "45%", fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, fontVariantNumeric: "tabular-nums", textAlign: "end" }}
					>
						{trailing}
					</Box>
				)
			)}
			{kind === "switch" && !loading && <SwitchIndicator isOn={isOn} />}
			{isChosen && !loading && <CheckRoundedIcon aria-hidden sx={{ flex: "none", fontSize: ICON_SIZE.MENU_ROW, color: "primary.main" }} />}
			{showChevron && <ChevronRightIcon aria-hidden sx={{ flex: "none", fontSize: ICON_SIZE.MENU_ROW, color: INK.MUTED }} />}
		</Box>
	);
};

/** The track-and-knob a `switch` row draws. Purely visual; the row carries `role="switch"` and `aria-checked`. */
const SwitchIndicator: FC<{ isOn: boolean }> = ({ isOn }) => {
	const theme = useTheme();
	const { TRACK_WIDTH, TRACK_HEIGHT, KNOB } = SHEET_ROW.SWITCH;
	const inset = (TRACK_HEIGHT - KNOB) / 2;
	return (
		<Box
			component="span"
			aria-hidden
			sx={{
				flex: "none",
				position: "relative",
				width: TRACK_WIDTH,
				height: TRACK_HEIGHT,
				borderRadius: `${TRACK_HEIGHT}px`,
				bgcolor: isOn ? theme.palette.primary.main : NEUTRAL[350],
				transition: `background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
				"@media (prefers-reduced-motion: reduce)": { transition: "none", "& > span": { transition: "none" } },
			}}
		>
			<Box
				component="span"
				sx={{
					position: "absolute",
					top: inset,
					left: inset,
					width: KNOB,
					height: KNOB,
					borderRadius: "50%",
					bgcolor: SURFACE,
					boxShadow: SHADOW.CONTROL_RAISED,
					transform: isOn ? `translateX(${TRACK_WIDTH - KNOB - inset * 2}px)` : "none",
					transition: `transform ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
				}}
			/>
		</Box>
	);
};
